import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { leadDrill } from "../lead_drill"
// Shared with coverage_gap_heatmap_controller. We only consume
// `formatHourLabel` here — Chart.js owns geometry and axis-label
// rendering for the matrix/bar charts below, so the geometry helpers in
// heatmap_helpers.js are intentionally not wired in. See the divergence
// note in heatmap_helpers.js before changing this.
import { formatHourLabel } from "./heatmap_helpers"

/**
 * Hourly Heatmap controller — three render modes (tiers / numbers / bars)
 * driven by a persisted user preference (ExhibitorUser#reports_heatmap_view).
 *
 * Inputs (all carried via data-* on the canvas):
 *   - data-multi-heatmap-payload-value: JSON string with cells, day_labels,
 *       hour_range, tier_bins, peaks, tz_abbrev, max_count, total_scans
 *   - data-multi-heatmap-view-value:    "tiers" | "numbers" | "bars"
 *   - data-multi-heatmap-update-url-value: PATCH endpoint persisting the
 *       selected view to the user record
 *   - data-multi-heatmap-csrf-value:    Rails CSRF token (request authenticity)
 *
 * Toggle wiring lives on sibling buttons: each button has
 *   data-action="click->multi-heatmap#switch"
 *   data-mode="tiers" (or numbers / bars)
 * The controller owns the active-button styling so the buttons stay
 * visually consistent across modes without partial reloads.
 */
export default class extends Controller {
  static values = {
    payload:   { type: String, default: "{}" },
    view:      { type: String, default: "tiers" },
    updateUrl: { type: String, default: "" },
    csrf:      { type: String, default: "" },
    canvasId:  { type: String, default: "" }
  }

  // Brand-aligned 6-stop sequential ramp. Keep these in sync with the SCSS
  // legend swatches in _chart_cards.scss so the inline legend matches the
  // rendered cells exactly.
  static SEQUENTIAL_RAMP = [
    "#EAF1F4",
    "#C5DAE2",
    "#93B6C5",
    "#6593A6",
    "#3F7188",
    "#295D77"
  ]

  // Discrete 5-tier swatch (None excluded — that's the hatched zero state).
  static TIER_RAMP = [
    "#EAF1F4", // Low
    "#B8CFD9", // 5–10
    "#6E94A6", // 11–18
    "#3F7188", // 19–28
    "#295D77"  // Peak
  ]

  // 6 time-of-day band colors for the stacked-bars mode. Mirrors the
  // mock — band-position drives hue, brand color anchors the centerpiece.
  static BAND_DEFS = [
    { key: "morning",    label: "Early Morning 8–10 AM",    range: [8, 9],   color: "#B8CFD9" },
    { key: "late_am",    label: "Late Morning 10 AM–12 PM", range: [10, 11], color: "#6E94A6" },
    { key: "midday",     label: "Early Afternoon 12–2 PM",  range: [12, 13], color: "#295D77" },
    { key: "afternoon",  label: "Afternoon 2–4 PM",         range: [14, 15], color: "#3F7188" },
    { key: "late_aft",   label: "Late Afternoon 4–6 PM",    range: [16, 17], color: "#6E94A6" },
    { key: "evening",    label: "Evening 6–8 PM",           range: [18, 19], color: "#B8CFD9" }
  ]

  static AMBER = "#F59E0B"

  connect() {
    // Bind document-level handlers once so add/removeEventListener reference
    // the same function (the project idiom — see reports_page_controller.js).
    this._onBeforeCache = this._handleBeforeCache.bind(this)
    this._onTurboRender = this._handleTurboRender.bind(this)

    this._payload = this._parsePayload()
    this._canvas = this._resolveCanvas()
    this._buildChart()
    this._syncToggleButtons(this.viewValue)
    this._renderLegend()
    // turbo:before-cache makes the cached snapshot inert (no Chart.js bound to
    // a soon-to-be-detached canvas). turbo:render rebuilds the chart AFTER that
    // teardown on the same navigation — see _handleTurboRender for the story.
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    document.addEventListener("turbo:render", this._onTurboRender)
  }

  // The controller binds to the chart card (parent of both the toggle
  // and the canvas) so a single click->multi-heatmap#switch handler can
  // resolve. The actual <canvas> we draw into is referenced by id via
  // `data-multi-heatmap-canvas-id-value`.
  _resolveCanvas() {
    if (this.canvasIdValue) {
      const node = document.getElementById(this.canvasIdValue)
      if (node instanceof HTMLCanvasElement) return node
    }
    if (this.element instanceof HTMLCanvasElement) return this.element
    return this.element.querySelector("canvas")
  }

  disconnect() {
    this._destroyChart()
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    document.removeEventListener("turbo:render", this._onTurboRender)
    // Null the bound refs (mirrors reports_page_controller.disconnect()) so the
    // controller holds no dangling references once detached.
    this._onBeforeCache = null
    this._onTurboRender = null
  }

  _handleBeforeCache() {
    this._destroyChart()
  }

  // Rebuild after a Turbo Drive page render.
  //
  // The Reports Hub tab links target the inner `reports_tab_content` Turbo
  // Frame AND carry `data-turbo-action="advance"`, so each tab click is a
  // frame swap that Turbo Drive also records in history. The observed Turbo
  // event order for landing on the Lead Activity tab is:
  //
  //   turbo:before-frame-render   → Stimulus disconnects the old controller
  //   turbo:frame-render          → connect() runs, the chart is BUILT here
  //   turbo:frame-load
  //   turbo:before-cache          → _handleBeforeCache DESTROYS the chart (so the
  //                                 cached snapshot is inert)
  //   turbo:before-render
  //   turbo:render                → (this handler) — rebuild what before-cache
  //                                 just tore down
  //   turbo:load
  //
  // Because connect() already fired at frame-render and won't run again on this
  // navigation, the before-cache destroy would otherwise leave the canvas with
  // NO chart — it collapses to Chart.js's default 300x150 and looks blank in
  // the 360px card. (On a cold load / hard refresh there's no before-cache in
  // the same cycle, so the chart survives — which is why the bug only appears
  // after tab-away-and-back.) Rebuilding on turbo:render — the Drive render
  // event that fires AFTER before-cache — restores the chart on every Drive
  // navigation. The rebuild is a no-op when the chart is already live, and is
  // skipped if the canvas is gone, so it can't double-render or leak.
  _handleTurboRender() {
    if (this.chart) return
    const canvas = this._resolveCanvas()
    if (!(canvas instanceof HTMLCanvasElement) || !canvas.isConnected) return
    this._canvas = canvas
    this._buildChart()
    this._syncToggleButtons(this.viewValue)
    this._renderLegend()
  }

  // -----------------------------------------------------------------
  // Public actions
  // -----------------------------------------------------------------

  // Click on a tier/number/bar toggle button. Optimistically swap the
  // chart, then persist via PATCH. If the server rejects, revert UI and
  // surface the error in an inline alert beneath the chart.
  switch(event) {
    if (event) event.preventDefault()
    const button = event.currentTarget
    const mode   = button?.dataset?.mode
    if (!mode || mode === this.viewValue) return

    const previous = this.viewValue
    this._setView(mode)
    this._persist(mode).catch((err) => {
      this._setView(previous)
      this._showError(err?.message || "Couldn't save your view preference. Please try again.")
    })
  }

  // -----------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------

  _setView(mode) {
    this.viewValue = mode
    this._destroyChart()
    this._buildChart()
    this._syncToggleButtons(mode)
    this._renderLegend()
    this._clearError()
  }

  _persist(mode) {
    if (!this.updateUrlValue) return Promise.resolve()
    return fetch(this.updateUrlValue, {
      method:      "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type":     "application/json",
        "Accept":           "application/json",
        "X-CSRF-Token":     this.csrfValue,
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ view: mode })
    }).then((res) => {
      if (res.ok) return true
      return res.text().then((text) => {
        let msg = "Couldn't save your view preference."
        try {
          const parsed = JSON.parse(text)
          msg = parsed.error || (parsed.errors && parsed.errors.join(", ")) || msg
        } catch (_) {
          // non-JSON error body — keep default
        }
        const err = new Error(msg)
        err.status = res.status
        throw err
      })
    })
  }

  // Locator for sibling DOM nodes (toggle group, legend, error host).
  // Each one is keyed by the canvas id so multiple heatmap cards can
  // coexist on the same page without leaking events.
  _scopedQuery(selector) {
    const id = this.canvasIdValue || this._canvas?.id
    if (!id) return null
    return this.element.querySelector(selector.replace("__ID__", id)) ||
           document.querySelector(selector.replace("__ID__", id))
  }

  _syncToggleButtons(activeMode) {
    const group = this._scopedQuery('[data-multi-heatmap-toggle-for="__ID__"]')
    if (!group) return
    group.querySelectorAll("[data-mode]").forEach((btn) => {
      const isActive = btn.dataset.mode === activeMode
      btn.classList.toggle("is-active", isActive)
      btn.setAttribute("aria-pressed", isActive ? "true" : "false")
    })
  }

  _showError(msg) {
    const target = this._errorTarget()
    if (!target) return
    target.textContent = msg
    target.classList.remove("d-none")
  }

  _clearError() {
    const target = this._errorTarget()
    if (!target) return
    target.textContent = ""
    target.classList.add("d-none")
  }

  _errorTarget() {
    return this._scopedQuery('[data-multi-heatmap-error-for="__ID__"]')
  }

  // Legend rendering — all DOM construction goes through createElement +
  // textContent / setAttribute. No innerHTML assignments anywhere; the
  // sourced strings are either constants or trusted server-emitted ints.
  _renderLegend() {
    const container = this._scopedQuery('[data-multi-heatmap-legend-for="__ID__"]')
    if (!container) return
    while (container.firstChild) container.removeChild(container.firstChild)

    const items = this._legendItems(this.viewValue)
    items.forEach((item) => container.appendChild(item))
  }

  _legendItems(mode) {
    const amber = this.constructor.AMBER

    if (mode === "numbers") {
      const ramp = this.constructor.SEQUENTIAL_RAMP
      const max  = this._payload.max_count || 0
      return [
        this._buildLegendGradient(ramp, max),
        this._buildLegendItem({ peakRing: amber, label: "Top 3 hours" }),
        this._buildLegendItem({ zero: true, label: "Zero activity" })
      ]
    }

    if (mode === "bars") {
      const items = this.constructor.BAND_DEFS.map((band) =>
        this._buildLegendItem({ swatchColor: band.color, label: band.label })
      )
      items.push(this._buildLegendItem({ peakRing: amber, label: "Peak band" }))
      return items
    }

    // tiers (default)
    const ramp = this.constructor.TIER_RAMP
    const bins = this._payload.tier_bins || {}
    const low  = bins.low  ?? 4
    const med  = bins.med  ?? 10
    const high = bins.high ?? 18
    const peak = bins.peak ?? 28
    return [
      this._buildLegendItem({ closed: true, label: "Closed" }),
      this._buildLegendItem({ zero: true,   label: "0 None" }),
      this._buildLegendItem({ swatchColor: ramp[0], label: `1–${low} Low` }),
      this._buildLegendItem({ swatchColor: ramp[1], label: `${low + 1}–${med}` }),
      this._buildLegendItem({ swatchColor: ramp[2], label: `${med + 1}–${high}` }),
      this._buildLegendItem({ swatchColor: ramp[3], label: `${high + 1}–${peak}` }),
      this._buildLegendItem({ swatchColor: ramp[4], label: `${peak + 1}+ Peak` }),
      this._buildLegendItem({ peakRing: amber, label: "Top 3 hours" })
    ]
  }

  _buildLegendItem({ swatchColor, closed, zero, peakRing, label }) {
    const wrap = document.createElement("span")
    wrap.className = "multi-heatmap-legend__item"

    const swatch = document.createElement("span")
    swatch.className = "multi-heatmap-legend__swatch"
    if (closed)   swatch.classList.add("multi-heatmap-legend__swatch--closed")
    if (zero)     swatch.classList.add("multi-heatmap-legend__swatch--zero")
    if (peakRing) {
      swatch.classList.add("multi-heatmap-legend__swatch--peak-ring")
      swatch.style.setProperty("--peak", peakRing)
    } else if (swatchColor) {
      swatch.style.background = swatchColor
    }
    wrap.appendChild(swatch)

    const text = document.createElement("span")
    text.textContent = label
    wrap.appendChild(text)

    return wrap
  }

  _buildLegendGradient(stops, maxValue) {
    const wrap = document.createElement("span")
    wrap.className = "multi-heatmap-legend__gradient"

    const min = document.createElement("span")
    min.className = "multi-heatmap-legend__min"
    min.textContent = "0"
    wrap.appendChild(min)

    const bar = document.createElement("span")
    bar.className = "multi-heatmap-legend__bar"
    bar.style.background = `linear-gradient(to right, ${stops.join(", ")})`
    wrap.appendChild(bar)

    const max = document.createElement("span")
    max.className = "multi-heatmap-legend__max"
    max.textContent = `${maxValue} scans`
    wrap.appendChild(max)

    return wrap
  }

  _parsePayload() {
    try {
      const parsed = JSON.parse(this.payloadValue || "{}")
      return {
        cells:       Array.isArray(parsed.cells) ? parsed.cells : [],
        day_labels:  Array.isArray(parsed.day_labels) ? parsed.day_labels : [],
        hour_range:  Array.isArray(parsed.hour_range) ? parsed.hour_range : [],
        tier_bins:   parsed.tier_bins || {},
        peaks:       new Set(Array.isArray(parsed.peaks) ? parsed.peaks : []),
        tz_abbrev:   parsed.tz_abbrev || "",
        max_count:   Number(parsed.max_count || 0),
        total_scans: Number(parsed.total_scans || 0)
      }
    } catch (e) {
      console.warn("multi-heatmap: failed to parse payload", e.message)
      return { cells: [], day_labels: [], hour_range: [], tier_bins: {}, peaks: new Set(), tz_abbrev: "", max_count: 0, total_scans: 0 }
    }
  }

  // -----------------------------------------------------------------
  // Chart construction — one builder per mode
  // -----------------------------------------------------------------
  _buildChart() {
    const canvas = this._canvas
    if (!(canvas instanceof HTMLCanvasElement)) return null
    const ctx = canvas.getContext("2d")
    let config

    switch (this.viewValue) {
      case "numbers":
        config = this._buildNumbersConfig()
        break
      case "bars":
        config = this._buildBarsConfig()
        break
      case "tiers":
      default:
        config = this._buildTiersConfig()
        break
    }

    // Canvas reuse guard. The turbo:render rebuild listener is document-level
    // while the no-op guard (`if (this.chart) return`) is per-INSTANCE, and the
    // canvas is resolved by the SHARED static id `la-hourly-heatmap`. On a
    // same-page Drive restoration a stale leaving instance (whose `this.chart`
    // was nulled at turbo:before-cache) can run _handleTurboRender → new Chart()
    // on the freshly-attached canvas right before the NEW instance builds on the
    // SAME canvas, and Chart.js v4 throws "Canvas is already in use…" → the
    // heatmap stays blank. Destroying whatever is bound to this canvas first is
    // the standard Chart.js reuse guard and closes that race regardless of how
    // teardown/build interleave.
    Chart.getChart(ctx.canvas)?.destroy()

    this.chart = new Chart(ctx, config)
    // Post-build sizing correction. When connect()/_setView() runs DURING a
    // Turbo restoration (tab-away-and-back), the 360px canvas wrapper isn't
    // laid out yet, so Chart.js measures 0/unknown and falls back to its
    // default 300x150 canvas. The wrapper's content-rect never subsequently
    // CHANGES, so Chart.js's own ResizeObserver never fires a correction and
    // the chart stays 300x150 — looking blank in the tall card. Re-measure the
    // (now laid-out) container once layout settles so the chart fills it.
    this._scheduleResizeCorrection()
    return this.chart
  }

  // Force Chart.js to re-measure its container after the current layout pass.
  // Robust against the Turbo frame-swap timing where the wrapper hasn't been
  // laid out at chart-build time:
  //   1. Double requestAnimationFrame — runs after the browser has performed
  //      at least one layout/paint of the restored DOM (a single rAF can fire
  //      before the swapped frame is laid out during a Turbo restoration).
  //   2. A one-shot ResizeObserver belt-and-suspenders — if the wrapper only
  //      resolves its real size AFTER both rAFs (late async layout / fonts),
  //      the observer catches the first non-empty box and corrects once.
  // Both are tracked on the instance and torn down centrally so nothing fires
  // on a destroyed/null chart and no observer/rAF leaks past the controller.
  _scheduleResizeCorrection() {
    this._cancelResizeCorrection()

    const resize = () => {
      // Guard: never resize a destroyed/null chart.
      this.chart?.resize()
    }

    // Schedule a double-rAF resize.
    if (typeof requestAnimationFrame === "function") {
      this._rafHandles = []
      const outer = requestAnimationFrame(() => {
        const inner = requestAnimationFrame(() => {
          this._rafHandles = null
          resize()
        })
        this._rafHandles = [inner]
      })
      this._rafHandles = [outer]
    } else {
      resize()
    }

    // One-shot ResizeObserver on the wrapper. observe() ALWAYS delivers an
    // initial callback with the element's CURRENT box (per spec) — during a
    // Turbo restoration that's the not-yet-laid-out 0-box, so we must IGNORE
    // empty boxes and keep observing until the first REAL (non-zero) box
    // arrives. Only then do we correct once and detach, so it can't leak or
    // keep firing. Guarded for environments without ResizeObserver.
    const wrapper = this._canvasWrapper()
    if (wrapper && typeof ResizeObserver === "function") {
      this._resizeObserver = new ResizeObserver((entries) => {
        const box = entries && entries[0] && entries[0].contentRect
        // Skip the spec-mandated initial 0-box (and any empty payload) — wait
        // for the wrapper to actually lay out before correcting.
        if (!box || box.width <= 0 || box.height <= 0) return
        // First meaningful layout — correct once and detach.
        this._teardownObserver()
        resize()
      })
      this._resizeObserver.observe(wrapper)
    }
  }

  _cancelResizeCorrection() {
    if (Array.isArray(this._rafHandles) && typeof cancelAnimationFrame === "function") {
      this._rafHandles.forEach((h) => cancelAnimationFrame(h))
    }
    this._rafHandles = null
    this._teardownObserver()
  }

  _teardownObserver() {
    if (this._resizeObserver) {
      try {
        this._resizeObserver.disconnect()
      } catch (_) {
        // ignore — observer may already be gone
      }
      this._resizeObserver = null
    }
  }

  // The 360px-tall positioned wrapper that the canvas fills. Chart.js sizes the
  // canvas to THIS element's content box, so it's what we observe/correct.
  _canvasWrapper() {
    const canvas = this._canvas
    if (canvas?.parentElement) return canvas.parentElement
    return this.element.querySelector(".multi-heatmap-canvas-wrapper")
  }

  _destroyChart() {
    // Cancel any pending resize correction first so it can never fire against
    // the chart we're about to destroy (or the next one we build).
    this._cancelResizeCorrection()
    try {
      this.chart?.destroy()
    } catch (_) {
      // ignore double-destroy
    }
    this.chart = null
  }

  // ---- TIERS (default) ---------------------------------------------
  _buildTiersConfig() {
    const { points, days, hours, peaks, tier_bins, tz_abbrev } = this._matrixPrep()
    const ramp = this.constructor.TIER_RAMP

    return {
      type: "matrix",
      data: {
        datasets: [{
          label: "Hourly leads",
          data: points,
          backgroundColor: (ctx) => {
            const v = +ctx.raw?.v || 0
            if (v === 0) return "rgba(245, 245, 245, 0.0)"
            return ramp[this._tierIndex(v, tier_bins)]
          },
          borderColor: (ctx) => {
            const k = this._cellKey(ctx.raw)
            return peaks.has(k) ? this.constructor.AMBER : "rgba(255, 255, 255, 0.6)"
          },
          borderWidth: (ctx) => peaks.has(this._cellKey(ctx.raw)) ? 2 : 1,
          width:  this._matrixWidthFn(days.length),
          height: this._matrixHeightFn(hours.length)
        }]
      },
      options: this._matrixOptions({ days, hours, peaks, tz_abbrev, showCounts: false })
    }
  }

  // ---- NUMBERS -----------------------------------------------------
  _buildNumbersConfig() {
    const { points, days, hours, peaks, max_count, tz_abbrev } = this._matrixPrep()
    const ramp = this.constructor.SEQUENTIAL_RAMP

    return {
      type: "matrix",
      data: {
        datasets: [{
          label: "Hourly leads",
          data: points,
          backgroundColor: (ctx) => {
            const v = +ctx.raw?.v || 0
            if (v === 0) return "rgba(245, 245, 245, 0.0)"
            const t = max_count > 0 ? Math.min(1, v / max_count) : 0
            return this._interpolate(ramp, t)
          },
          borderColor: (ctx) => peaks.has(this._cellKey(ctx.raw)) ? this.constructor.AMBER : "rgba(255, 255, 255, 0.7)",
          borderWidth: (ctx) => peaks.has(this._cellKey(ctx.raw)) ? 2 : 1,
          width:  this._matrixWidthFn(days.length),
          height: this._matrixHeightFn(hours.length)
        }]
      },
      options: this._matrixOptions({ days, hours, peaks, tz_abbrev, showCounts: true, max_count })
    }
  }

  // ---- BARS --------------------------------------------------------
  _buildBarsConfig() {
    const { day_labels, hour_range, cells, tz_abbrev } = this._payload
    const bands = this.constructor.BAND_DEFS

    // Map: dayKey -> bandKey -> count
    const totalsByDayBand = {}
    cells.forEach((c) => {
      if (!hour_range.includes(c.hour)) return
      const band = bands.find((b) => b.range.includes(c.hour))
      if (!band) return
      totalsByDayBand[c.date] ??= {}
      totalsByDayBand[c.date][band.key] = (totalsByDayBand[c.date][band.key] || 0) + (c.count || 0)
    })

    const dayTotals = day_labels.map((d) => ({
      key: d.key,
      total: bands.reduce((sum, b) => sum + ((totalsByDayBand[d.key] || {})[b.key] || 0), 0)
    }))
    const peakDay = dayTotals.slice().sort((a, b) => b.total - a.total)[0]?.key

    const labels = day_labels.map((d) => d.day_num)

    const datasets = bands.map((band) => ({
      label: band.label,
      data:  day_labels.map((d) => (totalsByDayBand[d.key] || {})[band.key] || 0),
      backgroundColor: band.color,
      borderColor: (ctx) => {
        const dKey = day_labels[ctx.dataIndex]?.key
        return dKey === peakDay ? this.constructor.AMBER : band.color
      },
      borderWidth: (ctx) => (day_labels[ctx.dataIndex]?.key === peakDay ? 2 : 0),
      stack: "day"
    }))

    return {
      type: "bar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 12 } },
        // Dormant unless the host element carries `data-drill` — same contract
        // as the matrix views' _onCellClick (the bars view previously had no
        // onClick, so it never drilled). A bar maps to a (day, time-band) pair:
        // dataIndex → day_labels[i].key (ISO date), datasetIndex →
        // BAND_DEFS[i].range (the band's integer hour list). See _onBarClick.
        onClick: this._onBarClick.bind(this),
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const idx = items[0]?.dataIndex ?? 0
                const lbl = day_labels[idx]
                if (!lbl) return ""
                return `${lbl.day_num} · ${lbl.day_date}`
              },
              label: (item) => {
                const total = dayTotals[item.dataIndex]?.total ?? 0
                return `${item.dataset.label}: ${item.raw} (${total} total)`
              },
              afterLabel: (item) => {
                if (!tz_abbrev) return ""
                return item.datasetIndex === bands.length - 1 ? `All times in ${tz_abbrev}` : ""
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              font: { weight: "bold" },
              callback: (val, idx) => labels[idx]
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { color: "rgba(0,0,0,0.06)" }
          }
        }
      }
    }
  }

  // -----------------------------------------------------------------
  // Shared matrix helpers
  // -----------------------------------------------------------------
  _matrixPrep() {
    const { day_labels, hour_range, cells, peaks, tier_bins, max_count, tz_abbrev, total_scans } = this._payload
    const days = day_labels.map((d) => d.day_num)
    // Hours are sorted ASCENDING (8, 9, 10, ...). Combined with `reverse: true`
    // on the category y-axis below, this lands the earliest hour at the top
    // ("morning at top"), which is the orientation the UX Architect flagged.
    const hours = hour_range.slice().sort((a, b) => a - b)

    const dayKeyByLabel = Object.fromEntries(day_labels.map((d) => [d.day_num, d.key]))
    const allowedHours = new Set(hours)

    const counts = {}
    cells.forEach((c) => {
      if (!allowedHours.has(c.hour)) return
      counts[`${c.date}|${c.hour}`] = c.count || 0
    })

    const points = []
    days.forEach((dayLabel) => {
      const dayKey = dayKeyByLabel[dayLabel]
      hours.forEach((h) => {
        const v = counts[`${dayKey}|${h}`] || 0
        points.push({ x: dayLabel, y: formatHourLabel(h), v, _dayKey: dayKey, _hour: h })
      })
    })

    return {
      points,
      days,
      hours: hours.map((h) => formatHourLabel(h)),
      peaks,
      tier_bins,
      max_count,
      tz_abbrev,
      total_scans,
      day_labels
    }
  }

  _matrixWidthFn(dayCount) {
    return ({ chart }) => {
      const area = chart.chartArea
      if (!area) return 30
      return (area.right - area.left) / Math.max(dayCount, 1) - 4
    }
  }

  _matrixHeightFn(hourCount) {
    return ({ chart }) => {
      const area = chart.chartArea
      if (!area) return 24
      return (area.bottom - area.top) / Math.max(hourCount, 1) - 4
    }
  }

  _matrixOptions({ days, hours, peaks, tz_abbrev, showCounts, max_count }) {
    const peakSet = peaks
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 8, bottom: 4 } },
      // Dormant unless the host element carries `data-drill`. CELL clicks
      // only — row/col axis-LABEL drills have no hit-test seam here and are
      // deferred to Phase 2. The clicked matrix cell carries `_dayKey`
      // (ISO date) and `_hour` (integer); the data-drill config decides
      // which of scan_date_exact / scan_hour to send.
      onClick: this._onCellClick.bind(this),
      plugins: {
        legend: { display: false },
        datalabels: {
          display: showCounts,
          color: (ctx) => {
            const v = +ctx.raw?.v || 0
            if (v === 0) return "transparent"
            const t = max_count > 0 ? Math.min(1, v / max_count) : 0
            return t > 0.5 ? "#FFFFFF" : "#1B1D26"
          },
          font: { size: 11, weight: "600" },
          formatter: (val) => (val?.v && val.v > 0 ? val.v : "")
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const p = items[0]?.raw
              if (!p) return ""
              return `${p.x} · ${p.y}${tz_abbrev ? ` ${tz_abbrev}` : ""}`
            },
            label: (item) => {
              const v = +item.raw?.v || 0
              const k = `${item.raw?._dayKey}|${item.raw?._hour}`
              const peak = peakSet.has(k)
              return `${v} lead${v === 1 ? "" : "s"}${peak ? " · top peak" : ""}`
            }
          }
        }
      },
      scales: {
        x: {
          type: "category",
          labels: days,
          // `offset: true` allocates a band per category so each matrix cell
          // centers WITHIN the band instead of straddling the category
          // gridline — without it the cells sit on the gridlines and overlap
          // the "Day 1 / Day 2 / Day 3" tick labels (LRPO-5205).
          offset: true,
          grid: { display: false },
          ticks: { font: { weight: "600" } }
        },
        y: {
          type: "category",
          labels: hours,
          // Chart.js category Y default is "first label at TOP", so an
          // ascending [8 AM, 9 AM, ...] puts 8 AM on top and the latest
          // hour at the bottom. That's exactly the "morning at top"
          // orientation the spec asks for — no `reverse: true` needed.
          reverse: false,
          // Same as the x axis: band the categories so cells center on each
          // hour row rather than overlapping the hour tick labels (LRPO-5205).
          offset: true,
          grid: { display: false },
          ticks: { autoSkip: false }
        }
      }
    }
  }

  // Matrix CELL click → leadDrill. Reads the clicked cell's day/hour
  // identifiers and maps them to drilldown params per the `data-drill`
  // config on the host element:
  //   data-drill = { origin, anchor, dateParam?, hourParam? }
  // dateParam defaults to "scan_date_exact", hourParam to "scan_hour".
  // Set a param key to false/null in the config to omit that dimension.
  _onCellClick(event, _elements, chart) {
    const drill = this.element.dataset.drill
    if (!drill) return
    let cfg
    try {
      cfg = JSON.parse(drill)
    } catch (e) {
      console.warn("multi-heatmap: data-drill is not valid JSON:", e.message)
      return
    }
    const hit = chart.getElementsAtEventForMode(event, "nearest", { intersect: true }, true)
    if (!hit.length) return
    const point = chart.data.datasets[hit[0].datasetIndex]?.data[hit[0].index]
    if (!point) return

    const filters = {}
    const dateParam = cfg.dateParam === undefined ? "scan_date_exact" : cfg.dateParam
    const hourParam = cfg.hourParam === undefined ? "scan_hour" : cfg.hourParam
    if (dateParam && point._dayKey != null) filters[dateParam] = point._dayKey
    if (hourParam && point._hour != null)   filters[hourParam] = point._hour
    if (!Object.keys(filters).length) return

    leadDrill(filters, { origin: cfg.origin, anchor: cfg.anchor })
  }

  // Bars (stacked time-of-day) click → leadDrill. Unlike the matrix cells, the
  // bar datapoints are plain counts, so we map the Chart.js hit's
  // dataIndex/datasetIndex back to identifiers ourselves:
  //   dataIndex    → day_labels[dataIndex].key   (the ISO date)
  //   datasetIndex → BAND_DEFS[datasetIndex]      (the time-of-day band)
  // The band's `.range` is the EXACT integer-hour list the bar was bucketed
  // from in _buildBarsConfig (cells matched via band.range.includes(hour)), so
  // filtering scan_hour to that list reconciles the drilled /leads list with the
  // clicked bar. Reads the same `data-drill` config as _onCellClick (dateParam
  // defaults "scan_date_exact", hourParam "scan_hour"); set either to false/null
  // in the config to omit that dimension. scan_hour is emitted as an ARRAY —
  // leadDrill serializes it to repeated `scan_hour[]=` params for the server's
  // `{ scan_hour: [] }` permit (LeadFilters), and a multi-hour band is the
  // whole point of this drill.
  _onBarClick(event, _elements, chart) {
    const drill = this.element.dataset.drill
    if (!drill) return
    let cfg
    try {
      cfg = JSON.parse(drill)
    } catch (e) {
      console.warn("multi-heatmap: data-drill is not valid JSON:", e.message)
      return
    }
    const hit = chart.getElementsAtEventForMode(event, "nearest", { intersect: true }, false)
    if (!hit.length) return
    const { index: dataIndex, datasetIndex } = hit[0]
    const dayKey    = this._payload.day_labels?.[dataIndex]?.key
    const bandHours = this.constructor.BAND_DEFS[datasetIndex]?.range

    const filters = {}
    const dateParam = cfg.dateParam === undefined ? "scan_date_exact" : cfg.dateParam
    const hourParam = cfg.hourParam === undefined ? "scan_hour" : cfg.hourParam
    if (dateParam && dayKey != null) filters[dateParam] = dayKey
    // Defensive copy (.slice) so leadDrill can never mutate the shared static
    // BAND_DEFS range. Array value → leadDrill emits repeated `scan_hour[]=`.
    if (hourParam && Array.isArray(bandHours) && bandHours.length) filters[hourParam] = bandHours.slice()
    if (!Object.keys(filters).length) return

    leadDrill(filters, { origin: cfg.origin, anchor: cfg.anchor })
  }

  _tierIndex(value, bins) {
    const ramp = this.constructor.TIER_RAMP
    const low  = bins?.low  ?? 4
    const med  = bins?.med  ?? 10
    const high = bins?.high ?? 18
    const peak = bins?.peak ?? 28
    if (value <= low)  return 0
    if (value <= med)  return 1
    if (value <= high) return 2
    if (value <= peak) return 3
    return ramp.length - 1
  }

  _cellKey(raw) {
    if (!raw) return ""
    return `${raw._dayKey}|${raw._hour}`
  }

  _interpolate(stops, t) {
    if (stops.length === 1) return stops[0]
    const scaled = t * (stops.length - 1)
    const i = Math.floor(scaled)
    const frac = scaled - i
    const a = this._hexToRgb(stops[i])
    const b = this._hexToRgb(stops[Math.min(i + 1, stops.length - 1)])
    const r = Math.round(a[0] + (b[0] - a[0]) * frac)
    const g = Math.round(a[1] + (b[1] - a[1]) * frac)
    const bl = Math.round(a[2] + (b[2] - a[2]) * frac)
    return `rgb(${r}, ${g}, ${bl})`
  }

  _hexToRgb(hex) {
    const h = String(hex).replace("#", "")
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
  }
}
